import {
  useSuspenseQuery,
  useInfiniteQuery,
  UseSuspenseQueryOptions,
  UseInfiniteQueryOptions,
} from "@tanstack/react-query";

import { Collection, CursorCollection } from "@/types";
import { api, ErrorResponse } from "@/utils/api";
import { camelize, type Camelize } from "@/utils/api-helpers";

export type UseSuspensePaginatedQueryOptions<T> = UseSuspenseQueryOptions<unknown, ErrorResponse, Collection<Camelize<T>>> & {
  url: string;
  query?: string;
  page?: number;
  limit?: number;
  orderby?: string;
  order?: "asc" | "desc";
};

export function useSuspensePaginatedQuery<T>({
  queryKey,
  url,
  query,
  page,
  limit,
  orderby,
  order,
  ...options
}: UseSuspensePaginatedQueryOptions<T>) {
  const params =
    Object
      .entries({ page, limit, orderby, order, query })
      .filter(([, value]) => value)
      .map(([key, value]) => `${key}=${value}`)

  return useSuspenseQuery<unknown, ErrorResponse, Collection<Camelize<T>>>({
    queryKey: [...queryKey, ...params],
    queryFn: async (): Promise<Collection<Camelize<T>>> => {
      const queryParams = params.length ? `?${params.join("&")}` : "";
      const response = await api.get<Collection<T>>(queryParams ? `${url}${queryParams}` : url);

      return camelize(response.data);
    },
    ...options
  });
}

export type UseInfinitePaginatedQueryOptions<T> = Omit<
  UseInfiniteQueryOptions<Collection<T>, ErrorResponse, Collection<T>>,
  "initialPageParam" | "getNextPageParam" | "getPreviousPageParam"
> & {
  url: string;
  query?: string;
  limit?: number;
  orderby?: string;
  order?: "asc" | "desc";
};

export function useInfinitePaginatedQuery<T>({
  queryKey,
  url,
  query,
  limit,
  orderby,
  order,
  ...options
}: UseInfinitePaginatedQueryOptions<T>) {
  const params =
    Object
      .entries({ limit, orderby, order, query })
      .filter(([, value]) => value)
      .map(([key, value]) => `${key}=${value}`)

  return useInfiniteQuery<Collection<T>, ErrorResponse, Collection<T>>({
    queryKey: [...queryKey, ...params], // TODO: add page param??
    queryFn: async ({ pageParam: page }): Promise<Collection<T>> => {
      const queryParams = `?${[`page=${page}`, ...params].join("&")}`;
      const response = await api.get<Collection<T>>(queryParams ? `${url}${queryParams}` : url);

      return response.data;
    },
    // select: (data: InfiniteData<Collection<T>, unknown>) => ({
    //   pages: data.pages,
    //   pageParams: data.pageParams,
    // }),
    initialPageParam: 1,
    getNextPageParam: (lastPage) => {
      if (lastPage.pageInfo.hasNextPage) {
        return lastPage.pageInfo.currentPage + 1;
      }
      return undefined;
    },
    getPreviousPageParam: (firstPage) => {
      if (firstPage.pageInfo.hasPreviousPage) {
        return firstPage.pageInfo.currentPage - 1;
      }
      return undefined;
    },
    ...options
  });
};

export type UseSuspenseCursorPaginatedQueryOptions<T> = UseSuspenseQueryOptions<unknown, ErrorResponse, CursorCollection<T>> & {
  url: string;
  cursor?: {
    after?: string;
    before?: string;
  },
};

export function useSuspenseCursorPaginatedQuery<T>({ queryKey, url, cursor, ...options }: UseSuspenseCursorPaginatedQueryOptions<T>) {
  const params =
    Object
      .entries(cursor || {})
      .filter(([, value]) => value)
      .map(([key, value]) => `${key}=${value}`)

  return useSuspenseQuery<unknown, ErrorResponse, CursorCollection<T>>({
    queryKey: [...queryKey, ...params],
    queryFn: async (): Promise<CursorCollection<T>> => {
      const query = params.length ? `?${params.join("&")}` : "";
      const response = await api.get<CursorCollection<T>>(query ? `${url}${query}` : url);

      return response.data;
    },
    ...options
  });
}
